import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import axios from 'axios'
import { format } from 'date-fns'
import { API_URL, AppConstant } from 'AppConstant'
import { convertUtcToEst } from '../../../../utils/DateAndTimeFormat'
import { APIStatus } from '../../../types'

export enum NoteStatus {
  ACCEPTED = 'accepted',
  PENDING = 'pending',
}

export interface INotes {
  id: string
  visitId: string
  chiropractorId: string
  subjective: string
  objective: string
  assessment: string
  plan: string
  status: string
  createdAt: string
  updatedAt: string
  patient: {
    id: string
    stripeCustomerId: string
    screeningStatus: string
    isQuestionareCompleted: boolean
    screenerFeedback: string
    screeningSessionRecording: string
    user: {
      id: string
      firstname: string
      lastname: string
      phone: string
      dob: string
      email: string
      profilePic: string
      screeningStatus: string
      gender: string
    }
  }
  isMember: boolean
}

interface NotesState {
  // Pending notes list (from polling API)
  notes: Array<INotes>
  notesStatus: APIStatus

  // Currently selected note (can be from pending list or history)
  currentNote: INotes | null

  // Track which pending-note card is selected (for UI highlight)
  selectedPendingNoteId: string | null

  // Current patient ID (for history panel - stays stable when selecting history notes)
  currentPatientId: string | null

  // Patient notes history (fetched per patient)
  patientNotes: {
    [patientId: string]: {
      notes: Array<INotes>
    }
  }
  patientNotesStatus: APIStatus

  // History note selection (when user clicks a history item)
  historySelectedNoteId: string | null

  // Local typing state for debounce (optimistic UI)
  // Keyed by noteId to handle multiple notes
  localNoteValues: {
    [noteId: string]: {
      subjective: string
      objective: string
      assessment: string
      plan: string
    }
  }

  // API statuses
  retryNotesStatus: APIStatus
  autoUpdateNoteStatus: APIStatus
  currentNoteUpdateStatus?: APIStatus
  patientNotesModalStatus: APIStatus

  // Legacy fields (can be removed if not used)
  retryNote: any
  autoUpdate: any
}

const initialState: NotesState = {
  // Pending notes
  notes: [],
  notesStatus: APIStatus.idle,

  // Current selection
  currentNote: null,
  selectedPendingNoteId: null,
  currentPatientId: null,
  historySelectedNoteId: null,

  // Patient history
  patientNotes: {},
  patientNotesStatus: APIStatus.idle,

  // Local typing state (for debounce)
  localNoteValues: {},

  // API statuses
  retryNotesStatus: APIStatus.idle,
  autoUpdateNoteStatus: APIStatus.idle,
  currentNoteUpdateStatus: undefined,
  patientNotesModalStatus: APIStatus.idle,

  // Legacy
  retryNote: null,
  autoUpdate: null,
}

export const getNotesAsync = createAsyncThunk('notes/getNotesAsync', async (patientId: string, thunkAPI) => {
  const token = localStorage.getItem(AppConstant.DOCTOR_TOKEN)

  let response = await axios
    .get(`${API_URL}/visit/patient/${patientId}/notes`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'X-Platform-Id': process.env.REACT_APP_PLATFORM_ID,
      },
    })
    .then((res) => res)
    .catch((err) => err.response)

  if (response.data && response.data.code === 'success') {
    return thunkAPI.fulfillWithValue({
      patientId,
      notes: response.data.data,
    })
  } else {
    return thunkAPI.rejectWithValue(response.data.message)
  }
})

export const getNotesByStatusAsync = createAsyncThunk(
  'notes/getNotesByStatusAsync',
  async ({ clinicId }: { clinicId: any }, thunkAPI) => {
    const token = localStorage.getItem(AppConstant.DOCTOR_TOKEN)

    let response = await axios
      .get(`${API_URL}/visit/notes/status?clinicId=${clinicId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Platform-Id': process.env.REACT_APP_PLATFORM_ID,
        },
      })
      .then((res) => res)
      .catch((err) => err.response)

    if (response.data && response.data.code === 'success') {
      return thunkAPI.fulfillWithValue(response.data.data)
    } else {
      return thunkAPI.rejectWithValue(response.data.message)
    }
  },
)

export const retryNotesAsync = createAsyncThunk(
  'notes/retryNotesAsync',
  async ({ notesId, noteToRetry }: { notesId: string; noteToRetry: string }, thunkAPI) => {
    const token = localStorage.getItem(AppConstant.DOCTOR_TOKEN)

    let response = await axios
      .put(
        `${API_URL}/clinic/note/${notesId}/${noteToRetry}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'X-Platform-Id': process.env.REACT_APP_PLATFORM_ID,
          },
        },
      )
      .then((res) => res)
      .catch((err) => err.response)

    if (response.data && response.data.code === 'success') {
      // The API returns the entire updated note object
      return thunkAPI.fulfillWithValue({
        updatedNote: response.data.data,
        noteId: notesId,
        noteToRetry: noteToRetry,
      })
    } else {
      return thunkAPI.rejectWithValue(response.data.message)
    }
  },
)

export const updateNotesAsync = createAsyncThunk(
  'notes/updateNotesByStatusAsync',
  async ({ updatedNotes, notes }: { updatedNotes: any; notes?: any }, thunkAPI) => {
    const token = localStorage.getItem(AppConstant.DOCTOR_TOKEN)
    const restofTheNotes = notes.filter((n: INotes) => n.id !== updatedNotes.id)
    //notes.status = NoteStatus.ACCEPTED

    let response = await axios
      .put(`${API_URL}/visit/notes/${updatedNotes.id}`, updatedNotes, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Platform-Id': process.env.REACT_APP_PLATFORM_ID,
        },
      })
      .then((res) => res)
      .catch((err) => err.response)

    if (response.data && response.data.code === 'success') {
      return thunkAPI.fulfillWithValue({
        currentNote: updatedNotes,
        notes: restofTheNotes,
      })
    } else {
      return thunkAPI.rejectWithValue(response.data.message)
    }
  },
)

export const autoUpdateNotesAsync = createAsyncThunk(
  'notes/autoUpdateNotesByStatusAsync',
  async ({ updatedNotes }: { updatedNotes: any }, thunkAPI) => {
    const token = localStorage.getItem(AppConstant.DOCTOR_TOKEN)
    //notes.status = NoteStatus.ACCEPTED
    let response = await axios
      .put(`${API_URL}/visit/notes/${updatedNotes.id}`, updatedNotes, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Platform-Id': process.env.REACT_APP_PLATFORM_ID,
        },
      })
      .then((res) => res)
      .catch((err) => err.response)
    if (response.data && response.data.code === 'success') {
      return thunkAPI.fulfillWithValue(response.data.data)
    } else {
      return thunkAPI.rejectWithValue(response.data.message)
    }
  },
)

export const notesSlice = createSlice({
  name: 'notes',
  initialState,
  reducers: {
    /**
     * NOTE IDENTIFICATION:
     * All notes are uniquely identified by noteId (note.id)
     * Multiple notes from the same patient are treated as separate, independent notes
     * - Pending notes list: Each note has unique noteId
     * - localNoteValues: Keyed by noteId (one entry per note)
     * - All updates: Find/update by noteId
     * This ensures no clashes when same patient appears multiple times in pending list
     */
    /**
     * Set current note from pending notes list
     * This is called when user clicks a note from the pending notes list
     *
     * IMPORTANT: Uses noteId (note.id) as the unique identifier
     * Multiple notes from the same patient are treated as separate notes
     * Each note has its own noteId and is stored/updated independently
     */
    setCurrentNote: (state, action) => {
      const selectedNote = action.payload

      // Find note from pendingNotes array by noteId (unique identifier)
      // Even if same patient has multiple notes, each is identified by its unique noteId
      const noteInPendingList = state.notes.find((note) => note.id === selectedNote?.id)

      if (noteInPendingList) {
        state.currentNote = noteInPendingList
        state.selectedPendingNoteId = noteInPendingList.id
        // Store patient ID for history panel (stays stable when selecting history notes)
        state.currentPatientId = noteInPendingList.patient?.id || null
        // Initialize local typing state for this specific note (keyed by noteId)
        // Each note has its own typing state, even if from the same patient
        if (!state.localNoteValues[noteInPendingList.id]) {
          state.localNoteValues[noteInPendingList.id] = {
            subjective: noteInPendingList.subjective || '',
            objective: noteInPendingList.objective || '',
            assessment: noteInPendingList.assessment || '',
            plan: noteInPendingList.plan || '',
          }
        }
      } else if (selectedNote) {
        state.currentNote = selectedNote
        state.selectedPendingNoteId = selectedNote.id
        // Store patient ID for history panel
        state.currentPatientId = selectedNote.patient?.id || null
        // Initialize local typing state
        if (!state.localNoteValues[selectedNote.id]) {
          state.localNoteValues[selectedNote.id] = {
            subjective: selectedNote.subjective || '',
            objective: selectedNote.objective || '',
            assessment: selectedNote.assessment || '',
            plan: selectedNote.plan || '',
          }
        }
      } else {
        state.currentNote = null
        state.currentPatientId = null
      }

      // Clear history selection when selecting from pending list
      state.historySelectedNoteId = null
    },

    /**
     * Set history note selection
     * This is called when user clicks a note from the history panel
     */
    setHistorySelectedNoteId: (state, action) => {
      const historyNoteId = action.payload
      state.historySelectedNoteId = historyNoteId

      // Find the history note in patientNotes and set it as currentNote
      // We need to find which patient this note belongs to
      // First try to use currentNote's patient ID, but also search all patientNotes if needed
      let patientId = state.currentNote?.patient?.id

      // If we don't have a patient ID from currentNote, search all patientNotes
      if (!patientId) {
        for (const pid in state.patientNotes) {
          const patientNotesArray = state.patientNotes[pid]?.notes
          if (patientNotesArray?.some((note) => note.id === historyNoteId)) {
            patientId = pid
            break
          }
        }
      }

      if (historyNoteId && patientId) {
        const patientNotesArray = state.patientNotes[patientId]?.notes
        if (patientNotesArray) {
          const historyNote = patientNotesArray.find((note) => note.id === historyNoteId)
          if (historyNote) {
            state.currentNote = historyNote
            // Keep the pending-note selection highlight unchanged
            // selectedPendingNoteId continues to point to the last pending note the user chose
            // Initialize local typing state for this note
            if (!state.localNoteValues[historyNote.id]) {
              state.localNoteValues[historyNote.id] = {
                subjective: historyNote.subjective || '',
                objective: historyNote.objective || '',
                assessment: historyNote.assessment || '',
                plan: historyNote.plan || '',
              }
            }
          }
        }
      }
    },

    /**
     * Update local typing state (for debounce)
     * This is called as user types, before API call
     *
     * IMPORTANT: Uses noteId as the unique key
     * Each note (even from same patient) has its own typing state
     */
    updateLocalNoteValue: (
      state,
      action: { payload: { noteId: string; field: 'subjective' | 'objective' | 'assessment' | 'plan'; value: string } },
    ) => {
      const { noteId, field, value } = action.payload
      // Each noteId has its own typing state object
      // Multiple notes from same patient = multiple entries in localNoteValues
      if (!state.localNoteValues[noteId]) {
        state.localNoteValues[noteId] = {
          subjective: '',
          objective: '',
          assessment: '',
          plan: '',
        }
      }
      state.localNoteValues[noteId][field] = value
    },

    /**
     * Reset all notes state
     * Called when component unmounts or pending notes list is empty
     */
    resetNotesState: (state) => {
      state.currentNote = null
      state.selectedPendingNoteId = null
      state.currentPatientId = null
      state.historySelectedNoteId = null
      state.localNoteValues = {}
      state.patientNotes = {}
      state.patientNotesStatus = APIStatus.idle
      state.retryNotesStatus = APIStatus.idle
      state.autoUpdateNoteStatus = APIStatus.idle
      state.currentNoteUpdateStatus = undefined
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getNotesAsync.fulfilled, (state, action) => {
      state.patientNotesStatus = APIStatus.fulfilled

      // Sort and filter patient notes
      const sortedNotes = [...action.payload.notes]
        .filter((note: any) => note.status === NoteStatus.PENDING || note.status === NoteStatus.ACCEPTED)
        .sort((a: any, b: any) => {
          const dateA = new Date(a.createdAt).getTime()
          const dateB = new Date(b.createdAt).getTime()
          return dateB - dateA
        })

      // Store patient notes in state
      state.patientNotes[action.payload.patientId] = {
        notes: sortedNotes,
      }

      // Initialize local typing state for all patient notes
      sortedNotes.forEach((note) => {
        if (!state.localNoteValues[note.id]) {
          state.localNoteValues[note.id] = {
            subjective: note.subjective || '',
            objective: note.objective || '',
            assessment: note.assessment || '',
            plan: note.plan || '',
          }
        }
      })

      // Preserve currentPatientId if it matches the loaded patient
      // This ensures history panel always shows notes for the selected pending note's patient
      if (
        state.currentPatientId === action.payload.patientId ||
        (!state.currentPatientId && state.currentNote?.patient?.id === action.payload.patientId)
      ) {
        // Don't change currentPatientId - keep it stable
        if (!state.currentPatientId) {
          state.currentPatientId = action.payload.patientId
        }

        // Do NOT auto-switch currentNote on history load (avoids flicker)
        // Instead, if the current note exists in history, highlight it; otherwise leave unselected.
        if (state.currentNote?.id) {
          const match = sortedNotes.find((note: any) => note.id === state.currentNote?.id)
          if (match) {
            state.historySelectedNoteId = match.id
          }
        }
      }
    })
    builder.addCase(getNotesAsync.rejected, (state, action) => {
      state.patientNotesStatus = APIStatus.rejected
    })
    builder.addCase(getNotesAsync.pending, (state, action) => {
      state.patientNotesStatus = APIStatus.pending
    })

    builder.addCase(retryNotesAsync.fulfilled, (state, action) => {
      const { updatedNote, noteId } = action.payload

      // Verify the note ID matches
      if (!updatedNote || updatedNote.id !== noteId) {
        state.retryNotesStatus = APIStatus.fulfilled
        return
      }

      const typedUpdatedNote = updatedNote as INotes

      // Helper: mutate existing note to avoid losing references (prevents UI flicker like image reloads)
      const applyNoteFields = (target?: INotes) => {
        if (!target) return
        target.subjective = typedUpdatedNote.subjective
        target.objective = typedUpdatedNote.objective
        target.assessment = typedUpdatedNote.assessment
        target.plan = typedUpdatedNote.plan
        target.status = typedUpdatedNote.status
        target.updatedAt = typedUpdatedNote.updatedAt
      }

      // Update the note in the pending notes array (state.notes) by noteId (mutate, don't replace)
      const noteIndexInPendingList = state.notes.findIndex((note) => note.id === noteId)
      if (noteIndexInPendingList !== -1) {
        applyNoteFields(state.notes[noteIndexInPendingList])
      }

      // Update the note in patientNotes if it exists
      // This is important for history notes - they come from patientNotes
      if (state.patientNotes && updatedNote.patient?.id) {
        const patientNotesArray = state.patientNotes[updatedNote.patient.id]?.notes
        if (patientNotesArray && Array.isArray(patientNotesArray)) {
          const noteIndexInPatientNotes = patientNotesArray.findIndex((note) => note.id === noteId)
          if (noteIndexInPatientNotes !== -1) {
            applyNoteFields(patientNotesArray[noteIndexInPatientNotes])
          }
        }
      }

      // Update currentNote if it matches the retried note ID
      // Priority: use from pendingNotes array if exists, otherwise from patientNotes
      if (state.currentNote && state.currentNote.id === noteId) {
        if (noteIndexInPendingList !== -1) {
          // Use the updated note from pendingNotes array (same reference) and mutate
          applyNoteFields(state.notes[noteIndexInPendingList])
          state.currentNote = state.notes[noteIndexInPendingList]
        } else if (updatedNote.patient?.id) {
          // If not in pending list, try to get from patientNotes (for history notes)
          const patientNotesArray = state.patientNotes[updatedNote.patient.id]?.notes
          if (patientNotesArray) {
            const noteInPatientNotes = patientNotesArray.find((note) => note.id === noteId)
            if (noteInPatientNotes) {
              applyNoteFields(noteInPatientNotes)
              state.currentNote = noteInPatientNotes
            } else {
              // Fallback: mutate currentNote directly
              applyNoteFields(state.currentNote)
            }
          } else {
            applyNoteFields(state.currentNote)
          }
        } else {
          applyNoteFields(state.currentNote)
        }

        // Update local typing state with the new values from API
        if (state.localNoteValues[noteId]) {
          state.localNoteValues[noteId] = {
            subjective: state.currentNote.subjective || '',
            objective: state.currentNote.objective || '',
            assessment: state.currentNote.assessment || '',
            plan: state.currentNote.plan || '',
          }
        }
      }

      state.retryNotesStatus = APIStatus.fulfilled
    })
    builder.addCase(retryNotesAsync.rejected, (state, action) => {
      state.retryNotesStatus = APIStatus.rejected
    })
    builder.addCase(retryNotesAsync.pending, (state, action) => {
      state.retryNotesStatus = APIStatus.pending
    })

    builder.addCase(getNotesByStatusAsync.fulfilled, (state, action) => {
      // Sort notes so that member notes (isMember: true) are first, non-members after
      state.notes = [...action.payload].sort((a: any, b: any) => {
        if (a.isMember === b.isMember) return 0
        return a.isMember ? -1 : 1
      })

      state.notesStatus = APIStatus.fulfilled

      // If there are no notes to approve, reset all state
      if (state.notes.length === 0) {
        state.currentNote = null
        state.historySelectedNoteId = null
        state.localNoteValues = {}
        state.patientNotes = {}
        state.patientNotesStatus = APIStatus.idle
        state.patientNotesModalStatus = APIStatus.idle
        state.retryNotesStatus = APIStatus.idle
        state.retryNote = null
        state.autoUpdateNoteStatus = APIStatus.idle
        state.autoUpdate = null
        state.currentNoteUpdateStatus = undefined
      }
    })
    builder.addCase(getNotesByStatusAsync.rejected, (state, action) => {
      state.notesStatus = APIStatus.rejected
    })
    builder.addCase(getNotesByStatusAsync.pending, (state, action) => {
      state.notesStatus = APIStatus.pending
    })

    builder.addCase(updateNotesAsync.fulfilled, (state, action) => {
      state.currentNoteUpdateStatus = APIStatus.fulfilled
      const approvedNote = action.payload.currentNote as INotes

      // Update pending notes list (already filtered in payload)
      state.notes = action.payload.notes
      // If the approved note was the selected pending note, clear the pending selection highlight
      if (state.selectedPendingNoteId === approvedNote?.id) {
        state.selectedPendingNoteId = null
      }

      // After approving, clear current selections so the next first pending note is selected/refetched by the hook
      if (action.payload.notes.length === 0) {
        // No pending notes left
        state.currentNote = null
        state.selectedPendingNoteId = null
        state.currentPatientId = null
        state.historySelectedNoteId = null
        state.patientNotes = {}
        state.patientNotesStatus = APIStatus.idle
        state.localNoteValues = {}
      } else {
        // Pending notes remain: clear current selections; useNotesScreen will pick first pending and fetch history
        state.currentNote = null
        state.selectedPendingNoteId = null
        state.currentPatientId = null
        state.historySelectedNoteId = null
        state.patientNotes = {}
        state.patientNotesStatus = APIStatus.idle
        state.localNoteValues = {}
      }

      state.currentNoteUpdateStatus = undefined
    })

    builder.addCase(updateNotesAsync.rejected, (state, action) => {
      state.currentNoteUpdateStatus = APIStatus.rejected
    })
    builder.addCase(updateNotesAsync.pending, (state, action) => {
      state.currentNoteUpdateStatus = APIStatus.pending
    })

    //auto update note
    builder.addCase(autoUpdateNotesAsync.fulfilled, (state, action) => {
      // state.autoUpdateNoteStatus = APIStatus.fulfilled
      // state.autoUpdate = action.payload
      // const updatedNote = action.payload
      // // Update note in pending notes array by noteId (unique identifier)
      // // Finds the exact note even if same patient has multiple notes
      // const noteIndexInPendingList = state.notes.findIndex((note) => note.id === updatedNote.id)
      // if (noteIndexInPendingList !== -1) {
      //   // Update fields directly (maintains reference)
      //   state.notes[noteIndexInPendingList].subjective = updatedNote.subjective
      //   state.notes[noteIndexInPendingList].objective = updatedNote.objective
      //   state.notes[noteIndexInPendingList].assessment = updatedNote.assessment
      //   state.notes[noteIndexInPendingList].plan = updatedNote.plan
      // }
      // // Update note in patientNotes (important for history notes)
      // if (state.patientNotes && updatedNote.patient?.id) {
      //   const patientNotesArray = state.patientNotes[updatedNote.patient.id]?.notes
      //   if (patientNotesArray) {
      //     const noteIndexInPatientNotes = patientNotesArray.findIndex((note) => note.id === updatedNote.id)
      //     if (noteIndexInPatientNotes !== -1) {
      //       patientNotesArray[noteIndexInPatientNotes].subjective = updatedNote.subjective
      //       patientNotesArray[noteIndexInPatientNotes].objective = updatedNote.objective
      //       patientNotesArray[noteIndexInPatientNotes].assessment = updatedNote.assessment
      //       patientNotesArray[noteIndexInPatientNotes].plan = updatedNote.plan
      //     }
      //   }
      // }
      // // Update currentNote if it matches
      // // Priority: use from pendingNotes array if exists, otherwise from patientNotes
      // if (state.currentNote && state.currentNote.id === updatedNote.id) {
      //   if (noteIndexInPendingList !== -1) {
      //     // Ensure currentNote points to the note in the pendingNotes array
      //     state.currentNote = state.notes[noteIndexInPendingList]
      //   } else if (updatedNote.patient?.id) {
      //     // If not in pending list, try to get from patientNotes (for history notes)
      //     const patientNotesArray = state.patientNotes[updatedNote.patient.id]?.notes
      //     if (patientNotesArray) {
      //       const noteInPatientNotes = patientNotesArray.find((note) => note.id === updatedNote.id)
      //       if (noteInPatientNotes) {
      //         state.currentNote = noteInPatientNotes
      //       } else {
      //         // Fallback: update fields directly
      //         state.currentNote.subjective = updatedNote.subjective
      //         state.currentNote.objective = updatedNote.objective
      //         state.currentNote.assessment = updatedNote.assessment
      //         state.currentNote.plan = updatedNote.plan
      //       }
      //     } else {
      //       // Fallback: update fields directly
      //       state.currentNote.subjective = updatedNote.subjective
      //       state.currentNote.objective = updatedNote.objective
      //       state.currentNote.assessment = updatedNote.assessment
      //       state.currentNote.plan = updatedNote.plan
      //     }
      //   } else {
      //     // Fallback: update fields directly
      //     state.currentNote.subjective = updatedNote.subjective
      //     state.currentNote.objective = updatedNote.objective
      //     state.currentNote.assessment = updatedNote.assessment
      //     state.currentNote.plan = updatedNote.plan
      //   }
      //   // Sync local typing state with updated values from API
      //   if (state.localNoteValues[updatedNote.id]) {
      //     state.localNoteValues[updatedNote.id] = {
      //       subjective: state.currentNote.subjective || '',
      //       objective: state.currentNote.objective || '',
      //       assessment: state.currentNote.assessment || '',
      //       plan: state.currentNote.plan || '',
      //     }
      //   }
      // }
    })
    builder.addCase(autoUpdateNotesAsync.rejected, (state, action) => {
      state.autoUpdateNoteStatus = APIStatus.rejected
    })
    builder.addCase(autoUpdateNotesAsync.pending, (state, action) => {
      state.autoUpdateNoteStatus = APIStatus.pending
    })
  },
})

export const { setCurrentNote, setHistorySelectedNoteId, updateLocalNoteValue, resetNotesState } = notesSlice.actions

export default notesSlice.reducer
